"use client";

import { GetUserSaves } from "@/user/actions/GetUserSaves";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { UserSaveViewModel } from "@/user/models/UserSaveViewModel";
import { AddUserSave } from "@/user/actions/AddUserSave";
import { RemoveUserSave } from "@/user/actions/RemoveUserSave";
import { useLazySession } from "./useLazySession";
import { useRouter } from "next/navigation";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";
import { UserSaveType } from "@/backend/user/models/UserSave";
import { UpdateUserSave } from "@/user/actions/UpdateUserSave";

type UserSavesContextStatus =
  | "awaiting_session"
  | "authentication_required"
  | "loading"
  | "refreshing"
  | "ready"
  | "saving";

interface UserSavesContext {
  userSaves: UserSaveViewModel[];
  status: UserSavesContextStatus;
  setUserSaves: (userSaves: UserSaveViewModel[]) => void;
  refreshUserSaves: () => Promise<void>;
}

const userSavesContext = createContext<UserSavesContext | undefined>({
  userSaves: [],
  status: "awaiting_session",
  setUserSaves: () => {},
  refreshUserSaves: async () => {},
});

export function UserSavesProvider({ children }: { children: React.ReactNode }) {
  const session = useLazySession();
  const [userSaves, setUserSaves] = useState<UserSaveViewModel[] | null>(null);
  const [status, setStatus] =
    useState<UserSavesContextStatus>("awaiting_session");

  const refreshUserSaves = useCallback(
    async (status: UserSavesContextStatus) => {
      try {
        setStatus(status);
        const userSaves = await GetUserSaves();
        setUserSaves(userSaves);
      } catch (ex) {
        console.warn("CLIENT_KNOWN_WARNING: Failed to refresh user saves", ex);
      } finally {
        setStatus("ready");
      }
    },
    [],
  );

  useEffect(() => {
    if (session.isLoaded) {
      if (session.isAuthenticated) {
        refreshUserSaves("loading");
      } else {
        setStatus("authentication_required");
      }
    }
  }, [session.isLoaded, session.isAuthenticated, refreshUserSaves]);

  const contextValue: UserSavesContext = {
    userSaves: userSaves ?? [],
    status,
    setUserSaves,
    refreshUserSaves: async () => await refreshUserSaves("refreshing"),
  };

  return (
    <userSavesContext.Provider value={contextValue}>
      {children}{" "}
    </userSavesContext.Provider>
  );
}

const ADD_USER_SAVE_PARAM = "addUserSave";
let ADD_USER_SAVE_PARAM_PROCESSED = false;

export function useUserSaveItem(item: UserSaveViewModel): {
  isSaved: boolean;
  isDisabled: boolean;
  ToggleSave: () => Promise<void>;
} {
  const [isSaving, setIsSaving] = useState(false);
  const context = useContext(userSavesContext);
  const router = useRouter();
  if (!context) {
    throw new Error("useUserSaves must be used within a UserSavesProvider");
  }

  const isSaved = context.userSaves.some(
    (save) => save.key === item.key && save.type === item.type,
  );

  const HandleAddUserSave = useCallback(
    async function (input: UserSaveViewModel) {
      if (!context) return;

      await AddUserSave(input);
      context.setUserSaves([...context.userSaves, input]);
      await context.refreshUserSaves();
    },
    [context],
  );

  const HandleRemoveUserSave = useCallback(
    async function (input: UserSaveViewModel) {
      if (!context) return;

      await RemoveUserSave(input);
      context.setUserSaves(
        context.userSaves.filter(
          (save) => save.key !== input.key || save.type !== input.type,
        ),
      );
      await context.refreshUserSaves();
    },
    [context],
  );

  const ToggleSave = useCallback(
    async function () {
      if (!context) return;

      if (context.status === "authentication_required") {
        router.push(`/auth`);
        return;
      }

      try {
        setIsSaving(true);
        const isSaved = context.userSaves.some(
          (save) => save.key === item.key && save.type === item.type,
        );
        const action = isSaved ? HandleRemoveUserSave : HandleAddUserSave;
        await action(item);
        CreateAnalyticEvent({
          event: {
            adIds: [item.key],
            eventType: isSaved ? "USER_SAVE_REMOVE" : "USER_SAVE_ADD",
            metadata: {
              itemId: item.key,
              itemType: item.type,
            },
          },
        });
      } catch {
        console.error("CLIENT_KNOWN_ERROR: Failed to save/unsave");
      } finally {
        setIsSaving(false);
      }
    },
    [context, item, router, HandleAddUserSave, HandleRemoveUserSave],
  );

  useEffect(() => {
    if (ADD_USER_SAVE_PARAM_PROCESSED) {
      return;
    }

    const queryString = window.location.search;
    const searchParams = new URLSearchParams(queryString);
    if (searchParams.get(ADD_USER_SAVE_PARAM) && context.status === "ready") {
      ADD_USER_SAVE_PARAM_PROCESSED = true;
      const decodedItem = JSON.parse(
        atob(searchParams.get(ADD_USER_SAVE_PARAM) || ""),
      ) as UserSaveViewModel;
      if (
        !isSaved &&
        decodedItem.key === item.key &&
        decodedItem.type === item.type
      ) {
        ToggleSave();
      }
      router.replace(window.location.pathname, {
        scroll: false,
      });
    }
  }, [context.status, isSaved, item.key, item.type, ToggleSave, router]);

  return {
    isSaved,
    isDisabled:
      context.status === "loading" ||
      context.status === "awaiting_session" ||
      isSaving,
    ToggleSave,
  };
}

export function useUserSaveType(type: UserSaveType): {
  userSaves: UserSaveViewModel[];
  status: UserSavesContextStatus;
  RemoveUserSave: (key: string) => Promise<void>;
  ToggleSubscribed: (key: string) => Promise<void>;
} {
  const context = useContext(userSavesContext);
  if (!context) {
    throw new Error("useUserSaves must be used within a UserSavesProvider");
  }

  return {
    userSaves: context.userSaves.filter((save) => save.type === type),
    status: context.status,
    RemoveUserSave: useCallback(
      async function (key: string) {
        if (!context) return;

        await RemoveUserSave({
          type,
          key,
        });
        context.setUserSaves(
          context.userSaves.filter(
            (save) => save.key !== key || save.type !== type,
          ),
        );
        await context.refreshUserSaves();
        CreateAnalyticEvent({
          event: {
            adIds: [key],
            eventType: "USER_SAVE_REMOVE",
            metadata: {
              itemId: key,
              itemType: type,
            },
          },
        });
      },
      [context, type],
    ),
    ToggleSubscribed: useCallback(
      async function (key: string) {
        if (!context) return;

        const save = context.userSaves.find(
          (save) => save.key === key && save.type === type,
        );
        if (!save) return;

        const updatedSave = {
          ...save,
          isUnsubscribed: !save.isUnsubscribed,
        };

        await UpdateUserSave(updatedSave);

        context.setUserSaves(
          context.userSaves.map((s) =>
            s.key === key && s.type === type ? updatedSave : s,
          ),
        );
        CreateAnalyticEvent({
          event: {
            adIds: [key],
            eventType: "USER_SAVE_UPDATE",
            metadata: {
              itemId: key,
              itemType: type,
              isUnsubscribed: updatedSave.isUnsubscribed,
            },
          },
        });
      },
      [context, type],
    ),
  };
}
